import {useMutation, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";

export interface BulkUpdateUserAuthoritiesDTO {
    userId: number;
    authorityGroupList: Array<number>;
}

const apiUrl = "api/users";
const apiUrlAuthorities = "/api/authority-groups";
const apiUrlUserAuthorities = "/api/user-authority-groups";

const fetchUsers = async (pagedSearchParams: PagedSearchParams) => {
    try {
        return await axios
            .get(apiUrl, {
                params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null,
                timeout: 30000
            })
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching echart data:", error);
        return Promise.reject(error);
    }
};

export const UsersReactQuery = (pagedSearchParams: PagedSearchParams) => {
    return useQuery({
        queryKey: ["data", pagedSearchParams],
        queryFn: () => {
            return fetchUsers(pagedSearchParams);
        }
    });
}

const saveUsers = async (data: any) => {
    try {
        if(data.id){
            return await axios.put(`${apiUrl}/${data.id}`, data,{timeout:30000}).then((res) => res.data);
        } else {
            return await axios.post(apiUrl, data,{timeout:30000}).then((res) => res.data);
        }
    } catch (error) {
        console.error("Error fetching users data:", error);
        return Promise.reject(error);
    }
};

export const SaveUsersReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (data: any) => {
            return saveUsers(data);
        },
        onSuccess: () => {
            enqueueSnackbar("Usuário salvo com sucesso!", { variant: "success" });
        },
        onError: (error: any) => {
            const detail = error?.response?.data?.detail;
            console.log(detail);
            
            enqueueSnackbar(detail ?? "Erro ao salvar usuário. Tente novamente.", { variant: "error" });
        }
    })
};

const deleteUsers = async (id: number) => {
    try {
        return await axios.delete(`${apiUrl}/${id}`,{timeout:30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const DeleteUsersReactQuery = (pagedSearchParams: PagedSearchParams) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) => {
            return deleteUsers(data.id);
        },
        onSuccess: () => {
            enqueueSnackbar("Usuário excluído com sucesso!", { variant: "success" });
            if (pagedSearchParams) {
                queryClient.invalidateQueries(["data", pagedSearchParams]);
            }
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao excluir usuário: ${error.message}`, { variant: "error" });
        }
    })
};

const fetchUsersById = async (id: number) => {
    try {
        return await axios.get(`${apiUrl}/${id}`,{timeout:30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching users data:", error);
        return Promise.reject(error);
    }
};

export const FindUsersByIdReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return fetchUsersById(id);
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao carregar um usuário: ${error.message}`, { variant: "error" });
        }
    })
};

const fetchNotificationGroup = async () => {
    try {
        return await axios.get("api/notification-groups").then((res) => res.data);
    } catch (error) {
        console.error("Error fetching notification group data:", error);
        return Promise.reject(error);
    }
};

export const FindNotificationGroupReactQuery = () => {
    return useQuery({
        queryKey: "notification-group",
        queryFn: () => {
            return fetchNotificationGroup();
        }
    });
};

const fetchAuthorities = async () => {
    const response = await axios.get(`${apiUrlAuthorities}`);
    return response.data.data;
};

const fetchAuthoritiesByUserId = async (userId:number) => {
    const response = await axios.get(`${apiUrlAuthorities}/find-by-user-id/${userId}`);
    return response.data.data;
};

const bulkUpdate = async (data:BulkUpdateUserAuthoritiesDTO) =>{
    try {
        return await axios.post(`${apiUrlUserAuthorities}/bulk-update`, data,{timeout:30000});
    } catch (error) {
        console.error("Error fetching users data:", error);
        return Promise.reject(error);
    }
}


export const AuthorityReactQuery = () => {
    const { data, isSuccess, refetch } = useQuery(
        "authorities",
        fetchAuthorities
    );

    return {
        authorities: data,
        authoritiesSuccess: isSuccess,
        executeManual: refetch
    };
}


export const AuthorityByUserIdReactQuery = (userId:number) => {
    return useQuery({
        queryKey: ["find-authority-group-by-user-id", userId],
        queryFn: () => {
            return fetchAuthoritiesByUserId(userId);
        },
        enabled: !!userId && userId > 0,
        refetchOnWindowFocus: true
    });
}

export const UpdateAuthorityGroupReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (data: BulkUpdateUserAuthoritiesDTO) => {
            return bulkUpdate(data);
        },
        onSuccess: () => {
            enqueueSnackbar("Associação realizada com sucesso!", { variant: "success" });
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao associar usuários: ${error.message}`, { variant: "error" });
        }
    })
};



export interface PowerStationWithPermission {
    id: number;
    name: string;
    hasPermission: boolean;
}

const fetchPowerStationsWithPermission = async (userId?: number): Promise<PowerStationWithPermission[]> => {
    const response = await axios.get("api/power-station-user/power-stations-with-permission", {
        params: { userId },
        timeout: 30000
    });
    return response.data.data;
};

export const FindPowerStationsWithPermissionReactQuery = (userId: number | undefined, enabled: boolean) => {
    const { enqueueSnackbar } = useSnackbar();
    return useQuery({
        queryKey: ["power-stations-with-permission", userId ?? "new"],
        queryFn: () => fetchPowerStationsWithPermission(userId),
        enabled,
        refetchOnWindowFocus: false,
        onError: () => {
            enqueueSnackbar("Erro ao carregar usinas.", { variant: "error" });
        }
    });
};
